(() => {
  'use strict';

  const THREE_BASE = 'https://esm.sh/three@0.185.0';
  const MB = 1024 * 1024;
  const LARGE_FILE = 12 * MB;
  const DESKTOP_AUTO_LIMIT = 75 * MB;
  const DESKTOP_HARD_LIMIT = 200 * MB;
  const MOBILE_HARD_LIMIT = 30 * MB;
  const DESKTOP_TRIANGLE_LIMIT = 4000000;
  const MOBILE_TRIANGLE_LIMIT = 350000;
  let modulesPromise;

  const q = (root, selector) => root.querySelector(selector);
  const ext = (file) => (file.name.split('.').pop() || '').toLowerCase();
  const formatMB = (bytes) => `${(bytes / MB).toFixed(bytes < 10 * MB ? 1 : 0)} MB`;
  const customSelected = (root) => Boolean(q(root, 'input[name^="shape-"][value="custom"]:checked'));
  const locale = (((window.Shopify && Shopify.locale) || document.documentElement.lang || 'en').toLowerCase().split('-')[0]);

  const COPY = {
    en: {
      title: 'Large 3D model',
      body: 'This file is larger than the automatic preview range. Previewing it may use significant browser memory.',
      keep: 'The file remains selected in Mold Studio either way.',
      preview: 'Preview model',
      mobile: 'This 3D model is too large for a safe browser preview on this device. Try a desktop computer with more memory.',
      hard: 'This 3D model is above the browser preview limit on this device.'
    },
    de: {
      title: 'Großes 3D-Modell',
      body: 'Diese Datei ist größer als der Bereich für die automatische Vorschau. Die Vorschau kann viel Browserspeicher beanspruchen.',
      keep: 'Die Datei bleibt in Mold Studio in jedem Fall ausgewählt.',
      preview: 'Modell anzeigen',
      mobile: 'Dieses 3D-Modell ist für eine sichere Browser-Vorschau auf diesem Gerät zu groß. Verwenden Sie einen Desktop-Computer mit mehr Arbeitsspeicher.',
      hard: 'Dieses 3D-Modell liegt über dem Browser-Vorschaulimit dieses Geräts.'
    },
    es: {
      title: 'Modelo 3D grande',
      body: 'Este archivo supera el rango de vista previa automática. Previsualizarlo puede usar bastante memoria del navegador.',
      keep: 'El archivo seguirá seleccionado en Mold Studio de cualquier forma.',
      preview: 'Previsualizar modelo',
      mobile: 'Este modelo 3D es demasiado grande para una vista previa segura en este dispositivo. Usa una computadora de escritorio con más memoria.',
      hard: 'Este modelo 3D supera el límite de vista previa del navegador en este dispositivo.'
    },
    fr: {
      title: 'Grand modèle 3D',
      body: 'Ce fichier dépasse la plage de prévisualisation automatique. Son aperçu peut utiliser beaucoup de mémoire du navigateur.',
      keep: 'Le fichier reste sélectionné dans Mold Studio dans tous les cas.',
      preview: 'Prévisualiser le modèle',
      mobile: 'Ce modèle 3D est trop volumineux pour une prévisualisation sûre sur cet appareil. Utilisez un ordinateur de bureau avec davantage de mémoire.',
      hard: 'Ce modèle 3D dépasse la limite de prévisualisation du navigateur sur cet appareil.'
    },
    it: {
      title: 'Modello 3D di grandi dimensioni',
      body: 'Questo file supera l’intervallo di anteprima automatica. La visualizzazione può utilizzare molta memoria del browser.',
      keep: 'Il file rimane comunque selezionato in Mold Studio.',
      preview: 'Visualizza modello',
      mobile: 'Questo modello 3D è troppo grande per un’anteprima sicura su questo dispositivo. Usa un computer desktop con più memoria.',
      hard: 'Questo modello 3D supera il limite di anteprima del browser su questo dispositivo.'
    },
    nl: {
      title: 'Groot 3D-model',
      body: 'Dit bestand is groter dan het bereik voor automatische preview. Het bekijken kan veel browsergeheugen gebruiken.',
      keep: 'Het bestand blijft hoe dan ook geselecteerd in Mold Studio.',
      preview: 'Model bekijken',
      mobile: 'Dit 3D-model is te groot voor een veilige browserpreview op dit apparaat. Gebruik een desktopcomputer met meer geheugen.',
      hard: 'Dit 3D-model ligt boven de browserpreviewlimiet van dit apparaat.'
    },
    pl: {
      title: 'Duży model 3D',
      body: 'Ten plik przekracza zakres automatycznego podglądu. Podgląd może zużywać dużo pamięci przeglądarki.',
      keep: 'Plik pozostanie wybrany w Mold Studio niezależnie od decyzji.',
      preview: 'Wyświetl model',
      mobile: 'Ten model 3D jest zbyt duży, aby bezpiecznie wyświetlić go na tym urządzeniu. Użyj komputera stacjonarnego z większą ilością pamięci.',
      hard: 'Ten model 3D przekracza limit podglądu przeglądarki na tym urządzeniu.'
    },
    ro: {
      title: 'Model 3D mare',
      body: 'Acest fișier depășește intervalul de previzualizare automată. Previzualizarea poate folosi multă memorie a browserului.',
      keep: 'Fișierul rămâne selectat în Mold Studio indiferent de opțiune.',
      preview: 'Previzualizează modelul',
      mobile: 'Acest model 3D este prea mare pentru o previzualizare sigură pe acest dispozitiv. Folosește un computer desktop cu mai multă memorie.',
      hard: 'Acest model 3D depășește limita de previzualizare a browserului pe acest dispozitiv.'
    },
    cs: {
      title: 'Velký 3D model',
      body: 'Tento soubor přesahuje rozsah automatického náhledu. Náhled může využít značné množství paměti prohlížeče.',
      keep: 'Soubor zůstane v Mold Studio vybraný v každém případě.',
      preview: 'Zobrazit model',
      mobile: 'Tento 3D model je pro bezpečný náhled v prohlížeči na tomto zařízení příliš velký. Použijte stolní počítač s větší pamětí.',
      hard: 'Tento 3D model překračuje limit náhledu prohlížeče na tomto zařízení.'
    },
    sk: {
      title: 'Veľký 3D model',
      body: 'Tento súbor presahuje rozsah automatického náhľadu. Náhľad môže využívať veľa pamäte prehliadača.',
      keep: 'Súbor zostane v Mold Studio vybraný bez ohľadu na voľbu.',
      preview: 'Zobraziť model',
      mobile: 'Tento 3D model je príliš veľký na bezpečný náhľad v prehliadači na tomto zariadení. Použite stolný počítač s väčšou pamäťou.',
      hard: 'Tento 3D model prekračuje limit náhľadu prehliadača na tomto zariadení.'
    },
    ja: {
      title: '大きな3Dモデル',
      body: 'このファイルは自動プレビューの範囲を超えています。プレビューには多くのブラウザメモリを使用する場合があります。',
      keep: 'プレビューしない場合でも、ファイルはMold Studioで選択されたままです。',
      preview: 'モデルをプレビュー',
      mobile: 'この3Dモデルは、この端末で安全にブラウザプレビューするには大きすぎます。より多くのメモリを備えたデスクトップPCをご利用ください。',
      hard: 'この3Dモデルは、この端末のブラウザプレビュー上限を超えています。'
    }
  };
  const copy = COPY[locale] || COPY.en;

  function status(root, message, isError = false) {
    const el = q(root, '[data-status]');
    if (!el) return;
    el.textContent = message;
    el.dataset.state = isError ? 'error' : 'success';
  }

  function deviceProfile() {
    const coarsePointer = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const narrowScreen = window.matchMedia && window.matchMedia('(max-width: 900px)').matches;
    const isMobile = Boolean(coarsePointer && narrowScreen);
    const memory = Number(navigator.deviceMemory || 0);

    if (isMobile) {
      let hardBytes = MOBILE_HARD_LIMIT;
      let hardTriangles = MOBILE_TRIANGLE_LIMIT;
      if (memory > 0 && memory <= 2) {
        hardBytes = 15 * MB;
        hardTriangles = 150000;
      } else if (memory > 0 && memory <= 4) {
        hardBytes = 25 * MB;
        hardTriangles = 250000;
      }
      return { isMobile: true, autoBytes: hardBytes, hardBytes, hardTriangles };
    }

    let autoBytes = DESKTOP_AUTO_LIMIT;
    let hardBytes = DESKTOP_HARD_LIMIT;
    let hardTriangles = DESKTOP_TRIANGLE_LIMIT;
    if (memory > 0 && memory <= 2) {
      autoBytes = 20 * MB;
      hardBytes = 60 * MB;
      hardTriangles = 800000;
    } else if (memory > 0 && memory <= 4) {
      autoBytes = 40 * MB;
      hardBytes = 120 * MB;
      hardTriangles = 2000000;
    }
    return { isMobile: false, autoBytes, hardBytes, hardTriangles };
  }

  async function mods() {
    return modulesPromise || (modulesPromise = Promise.all([
      import(THREE_BASE),
      import(`${THREE_BASE}/examples/jsm/loaders/OBJLoader.js`),
      import(`${THREE_BASE}/examples/jsm/loaders/STLLoader.js`),
      import(`${THREE_BASE}/examples/jsm/controls/OrbitControls.js`)
    ]).then(([THREE, OBJ, STL, Controls]) => ({
      THREE,
      OBJLoader: OBJ.OBJLoader,
      STLLoader: STL.STLLoader,
      OrbitControls: Controls.OrbitControls
    })));
  }

  function ensureStyles() {
    if (document.getElementById('gm-model-preview-guard-styles')) return;
    const style = document.createElement('style');
    style.id = 'gm-model-preview-guard-styles';
    style.textContent = `
      [data-gmb] .gmb__model-guard{position:absolute;inset:0;z-index:12;display:flex;align-items:center;justify-content:center;padding:28px;background:rgba(242,245,246,.96)}
      [data-gmb] .gmb__model-guard[hidden]{display:none!important}
      [data-gmb] .gmb__model-guard-card{width:min(620px,100%);padding:24px 24px 22px;border:1px solid #e2c477;border-radius:16px;background:#fffaf0;box-shadow:0 16px 34px rgba(21,25,29,.12);text-align:center}
      [data-gmb] .gmb__model-guard-card strong{display:block;color:#4b3510;font-size:18px;line-height:1.3}
      [data-gmb] .gmb__model-guard-card p{margin:9px 0 0;color:#655435;font-size:13px;line-height:1.55}
      [data-gmb] .gmb__model-guard-card small{display:block;margin:7px 0 0;color:#78694e;font-size:11px;line-height:1.45}
      [data-gmb] .gmb__model-guard-card button{position:static!important;float:none!important;transform:none!important;display:inline-flex!important;align-items:center!important;justify-content:center!important;min-width:150px!important;min-height:44px!important;margin:16px 0 0!important;padding:10px 16px!important;border:0!important;border-radius:9px!important;background:#24272d!important;color:#fff!important;font-size:12px!important;line-height:1.2!important;font-weight:900!important;cursor:pointer!important}
      @media(max-width:520px){[data-gmb] .gmb__model-guard{padding:16px}[data-gmb] .gmb__model-guard-card{padding:20px 16px}[data-gmb] .gmb__model-guard-card button{width:100%!important}}
    `;
    document.head.appendChild(style);
  }

  function overlay(root) {
    const canvasWrap = q(root, '.gmb__canvas');
    let node = canvasWrap && q(canvasWrap, '[data-gm3d]');
    if (!canvasWrap) return null;

    if (!node) {
      node = document.createElement('div');
      node.className = 'gmb__model-viewer';
      node.dataset.gm3d = '';
      node.hidden = true;
      node.innerHTML = '<div class="gmb__model-loading" data-load hidden><span></span><strong>Loading 3D model...</strong></div><div class="gmb__model-error" data-error hidden></div><div class="gmb__model-guard" data-guard hidden><div class="gmb__model-guard-card"><strong data-guard-title></strong><p data-guard-copy></p><small data-guard-note></small><button type="button" data-preview-large></button></div></div><span class="gmb__model-badge" data-model-badge hidden></span>';
      canvasWrap.append(node);
    }

    return node;
  }

  function title(root, active) {
    const heading = q(root, '.gmb__visual-title b');
    const subheading = q(root, '.gmb__visual-title small');
    if (!heading || !subheading) return;
    heading.textContent = active ? 'Uploaded 3D Model Preview' : '● Engineering Preview';
    subheading.textContent = active ? 'Drag to rotate · scroll or pinch to zoom · reference only' : 'Preliminary arrangement · not production CAD';
  }

  function disposeObject(object) {
    if (!object || typeof object.traverse !== 'function') return;
    object.traverse((node) => {
      if (node.geometry && typeof node.geometry.dispose === 'function') node.geometry.dispose();
      const materials = Array.isArray(node.material) ? node.material : [node.material];
      materials.filter(Boolean).forEach((material) => material.dispose && material.dispose());
    });
  }

  function clear(root) {
    const state = root._gm3d;
    if (state) {
      state.controls?.dispose();
      state.resizeObserver?.disconnect();
      state.renderer?.dispose();
      state.renderer?.forceContextLoss?.();
      disposeObject(state.object);
      state.overlay?.querySelector('canvas')?.remove();
    }
    root._gm3d = null;

    const node = q(root, '[data-gm3d]');
    if (node) {
      node.hidden = true;
      q(node, '[data-load]').hidden = true;
      q(node, '[data-error]').hidden = true;
      q(node, '[data-guard]').hidden = true;
      q(node, '[data-model-badge]').hidden = true;
    }
    title(root, false);
  }

  function fail(root, message) {
    root._gmFootToken = (root._gmFootToken || 0) + 1;
    const node = overlay(root);
    if (!node) return;
    node.hidden = false;
    q(node, '[data-load]').hidden = true;
    q(node, '[data-guard]').hidden = true;
    const error = q(node, '[data-error]');
    error.hidden = false;
    error.textContent = message;
    status(root, message, true);
    root.dispatchEvent(new CustomEvent('gm:model-failed', { bubbles: false, detail: { message } }));
  }

  function loading(root, file) {
    const node = overlay(root);
    if (!node) return;
    node.hidden = false;
    q(node, '[data-error]').hidden = true;
    q(node, '[data-guard]').hidden = true;
    const loadingNode = q(node, '[data-load]');
    loadingNode.hidden = false;
    q(loadingNode, 'strong').textContent = file.size > LARGE_FILE
      ? `Loading ${file.name} (${formatMB(file.size)})... large models can take a little longer.`
      : `Loading ${file.name}...`;
  }

  function selectCustom3D(root) {
    const custom = q(root, 'input[name^="shape-"][value="custom"]');
    if (custom && !custom.checked) {
      custom.checked = true;
      custom.dispatchEvent(new Event('change', { bubbles: true }));
    }

    ['[data-mode="gummy"]', '[data-view="3d"]'].forEach((selector) => {
      const button = q(root, selector);
      if (button && !button.classList.contains('on')) button.click();
    });
  }

  function showLargeGuard(root, file, profile) {
    clear(root);
    selectCustom3D(root);
    const node = overlay(root);
    if (!node) return;
    node.hidden = false;
    const guard = q(node, '[data-guard]');
    guard.hidden = false;
    q(node, '[data-error]').hidden = true;
    q(node, '[data-load]').hidden = true;
    q(guard, '[data-guard-title]').textContent = `${copy.title} · ${formatMB(file.size)}`;
    q(guard, '[data-guard-copy]').textContent = `${copy.body} Automatic preview is enabled up to ${formatMB(profile.autoBytes)} on this desktop.`;
    q(guard, '[data-guard-note]').textContent = copy.keep;
    const button = q(guard, '[data-preview-large]');
    button.textContent = copy.preview;
    button.onclick = () => {
      if (q(root, '[data-in="file"]')?.files?.[0] !== file) return;
      root.dispatchEvent(new CustomEvent('gm:model-preview-requested', { bubbles: false, detail: { fileName: file.name, fileSize: file.size } }));
      load(root, file, true);
    };
    status(root, '');
  }

  function triangleCount(object) {
    let count = 0;
    object.traverse((node) => {
      const geometry = node.geometry;
      if (!geometry?.attributes?.position) return;
      count += Math.floor((geometry.index?.count || geometry.attributes.position.count) / 3);
    });
    return count;
  }

  async function parse(file, modules) {
    if (ext(file) === 'obj') {
      const text = await file.text();
      const object = new modules.OBJLoader().parse(text);
      if (!triangleCount(object)) throw new Error('OBJ contains no triangulated mesh.');
      return object;
    }

    const geometry = new modules.STLLoader().parse(await file.arrayBuffer());
    if (!geometry?.attributes?.position?.count) throw new Error('STL contains no mesh.');
    if (!geometry.attributes.normal) geometry.computeVertexNormals();
    return new modules.THREE.Group().add(new modules.THREE.Mesh(geometry));
  }

  function scene(root, modules, object, file, triangles) {
    const THREE = modules.THREE;
    const node = overlay(root);
    const canvasWrap = q(root, '.gmb__canvas');
    const scene3D = new THREE.Scene();
    scene3D.background = new THREE.Color(0xf2f5f6);

    const camera = new THREE.PerspectiveCamera(36, 1, 0.01, 100);
    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    node.prepend(renderer.domElement);

    scene3D.add(new THREE.HemisphereLight(0xffffff, 0x52606b, 2.5));
    const key = new THREE.DirectionalLight(0xffffff, 4);
    key.position.set(4, 6, 7);
    scene3D.add(key);

    object.traverse((mesh) => {
      if (!mesh.isMesh) return;
      if (mesh.geometry && !mesh.geometry.attributes.normal) mesh.geometry.computeVertexNormals();
      const oldMaterials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      oldMaterials.filter(Boolean).forEach((material) => material.dispose && material.dispose());
      mesh.material = new THREE.MeshPhysicalMaterial({
        color: 0x15905a,
        roughness: 0.34,
        clearcoat: 0.18,
        side: THREE.DoubleSide
      });
    });

    const bounds = new THREE.Box3().setFromObject(object);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    bounds.getSize(size);
    bounds.getCenter(center);
    const maxDimension = Math.max(size.x, size.y, size.z);
    if (!Number.isFinite(maxDimension) || maxDimension <= 0) throw new Error('Model dimensions are invalid.');

    object.position.sub(center);
    object.scale.setScalar(3.2 / maxDimension);
    scene3D.add(object);

    const controls = new modules.OrbitControls(camera, renderer.domElement);
    controls.enableDamping = false;
    controls.enablePan = false;
    controls.minDistance = 3.2;
    controls.maxDistance = 13;

    const render = () => renderer.render(scene3D, camera);
    controls.addEventListener('change', render);

    const setCamera = (top) => {
      camera.position.set(...(top ? [0, 7.4, 0.001] : [4.8, 3.7, 6.1]));
      camera.up.set(...(top ? [0, 0, -1] : [0, 1, 0]));
      camera.lookAt(0, 0, 0);
      controls.target.set(0, 0, 0);
      controls.update();
      render();
    };

    const resize = () => {
      const width = Math.max(1, canvasWrap.clientWidth);
      const height = Math.max(1, canvasWrap.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      render();
    };

    setCamera(Boolean(q(root, '[data-view="top"].on')));
    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvasWrap);

    q(node, '[data-load]').hidden = true;
    q(node, '[data-error]').hidden = true;
    q(node, '[data-guard]').hidden = true;
    const badge = q(node, '[data-model-badge]');
    badge.textContent = `${file.name} · ${formatMB(file.size)} · ${size.x.toFixed(2)} × ${size.y.toFixed(2)} × ${size.z.toFixed(2)} model units · ${triangles.toLocaleString()} triangles`;
    badge.hidden = false;

    root._gm3d = { overlay: node, object, controls, resizeObserver, renderer, setCamera };
    node.hidden = !(customSelected(root) && q(root, '[data-mode="gummy"].on'));
    title(root, !node.hidden);
    root.dispatchEvent(new CustomEvent('gm:model-ready', { bubbles: false, detail: { fileName: file.name, fileSize: file.size, triangles } }));
    root.dispatchEvent(new CustomEvent('gm:model-loaded', { bubbles: false, detail: { fileName: file.name, fileSize: file.size, triangles } }));
  }

  async function load(root, file, forceLarge = false) {
    const token = root._gmToken = (root._gmToken || 0) + 1;
    if (!['obj', 'stl'].includes(ext(file))) {
      clear(root);
      status(root, window.GMBTranslateText?.('Reference file selected. Interactive 3D preview is available for OBJ or STL files.') || 'Reference file selected. Interactive 3D preview is available for OBJ or STL files.');
      return;
    }

    const profile = deviceProfile();
    if (file.size > profile.hardBytes) {
      clear(root);
      const message = `${profile.isMobile ? copy.mobile : copy.hard} ${copy.keep}`;
      fail(root, message);
      return;
    }

    if (!profile.isMobile && file.size > profile.autoBytes && !forceLarge) {
      showLargeGuard(root, file, profile);
      return;
    }

    selectCustom3D(root);
    loading(root, file);
    status(root, `Loading uploaded 3D model (${formatMB(file.size)})...`);

    let sourceObject;
    try {
      await new Promise((resolve) => setTimeout(resolve, 30));
      const modules = await mods();
      if (token !== root._gmToken) return;

      sourceObject = await parse(file, modules);
      if (token !== root._gmToken || q(root, '[data-in="file"]')?.files?.[0] !== file) {
        disposeObject(sourceObject);
        return;
      }

      const triangles = triangleCount(sourceObject);
      if (triangles > profile.hardTriangles) {
        disposeObject(sourceObject);
        sourceObject = null;
        throw new Error(`This model contains ${triangles.toLocaleString()} triangles, above this device's interactive-preview limit of ${profile.hardTriangles.toLocaleString()}.`);
      }

      clear(root);
      scene(root, modules, sourceObject, file, triangles);
      sourceObject = null;
      status(root, `${file.name} loaded. Drag to rotate; scroll or pinch to zoom. Model units are reference-only until you confirm the finished physical size.`);
    } catch (error) {
      if (sourceObject) disposeObject(sourceObject);
      clear(root);
      fail(root, `${error?.message || 'Unable to preview this file.'} The source file remains selected in Mold Studio but is not attached to the quote; Gummy Molds can request it separately if needed.`);
    }
  }

  function sync(root) {
    const state = root._gm3d;
    const node = q(root, '[data-gm3d]');
    if (!node) return;
    const guardVisible = !q(node, '[data-guard]')?.hidden;
    if (guardVisible) {
      node.hidden = !(customSelected(root) && q(root, '[data-mode="gummy"].on'));
      return;
    }
    const active = Boolean(state && customSelected(root) && q(root, '[data-mode="gummy"].on'));
    node.hidden = !active;
    title(root, active);
    if (active) state.setCamera(Boolean(q(root, '[data-view="top"].on')));
  }

  function init(root) {
    if (root.dataset.gm3dReady) return;
    root.dataset.gm3dReady = '1';
    ensureStyles();
    overlay(root);

    const input = q(root, '[data-in="file"]');
    input?.addEventListener('change', () => {
      const file = input.files?.[0];
      if (file) load(root, file);
      else {
        root._gmToken = (root._gmToken || 0) + 1;
        root._gmFootToken = (root._gmFootToken || 0) + 1;
        clear(root);
        status(root, '');
      }
    });

    root.addEventListener('change', (event) => {
      if (event.target.matches('input[name^="shape-"]')) setTimeout(() => sync(root), 0);
    });

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-mode],[data-view]')) setTimeout(() => sync(root));
      if (event.target.closest('[data-reset]') && root._gm3d) {
        setTimeout(() => {
          root._gm3d?.setCamera(false);
          sync(root);
        });
      }
    });
  }

  function boot() {
    document.querySelectorAll('[data-gmb]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
